import { CommonModule } from '@angular/common';
import { Component, Input } from '@angular/core';

type ProgressIndicatorVariant = 'overlay' | 'top-bar' | 'content';
type ProgressIndicatorAppearance = 'elevated' | 'flat';
type ProgressIndicatorSize = 'regular' | 'compact';
type ProgressIndicatorSelection = 'standard' | 'brand-halo';
type ProgressPreviewKind =
  | 'rail'
  | 'pulse'
  | 'scan'
  | 'trace'
  | 'steps'
  | 'brand-orbit'
  | 'brand-glide'
  | 'brand-signal'
  | 'brand-echo'
  | 'brand-scan'
  | 'brand-beacon'
  | 'brand-halo'
  | 'brand-draft'
  | 'brand-build'
  | 'brand-door'
  | 'brand-outline'
  | 'logo-r-roof'
  | 'logo-r-core'
  | 'logo-block-home'
  | 'logo-parcel-cut'
  | 'logo-contour'
  | 'logo-terrace';

@Component({
  selector: 'app-progress-indicator',
  standalone: true,
  imports: [CommonModule],
  template: `
    <ng-container [ngSwitch]="variant">
      <div *ngSwitchCase="'top-bar'" class="progress-topbar" role="progressbar" [attr.aria-label]="label">
        <span class="progress-track"></span>
        <span class="progress-indeterminate"></span>
      </div>

      <div
        *ngSwitchCase="'content'"
        class="progress-content"
        [class.progress-content--compact]="size === 'compact'"
        [class.progress-content--flat]="appearance === 'flat'"
        [class.progress-content--showcase]="showcaseOptions"
        [class.progress-content--selected]="contentSelection !== 'standard' && !showcaseOptions"
        role="status"
        aria-live="polite"
        [attr.aria-label]="label"
      >
        <div class="progress-content-stage" aria-hidden="true">
          <ng-container *ngIf="showcaseOptions; else progressSelectedShell">
            <div class="progress-showcase">
              <div class="progress-showcase__section">
                <div class="progress-showcase__heading">Flat Concepts</div>
                <div class="progress-showcase__grid progress-showcase__grid--concept">
                  <div *ngFor="let option of conceptOptions" class="progress-showcase__item">
                    <span class="progress-showcase__index">{{ option.id }}</span>
                    <span class="progress-showcase__name">{{ option.name }}</span>

                    <ng-container [ngSwitch]="option.kind">
                      <span *ngSwitchCase="'rail'" class="progress-option-rail">
                        <span class="progress-option-rail__track"></span>
                        <span class="progress-option-rail__bar"></span>
                      </span>

                      <span *ngSwitchCase="'pulse'" class="progress-option-pulse">
                        <span class="progress-option-pulse__bar progress-option-pulse__bar--first"></span>
                        <span class="progress-option-pulse__bar progress-option-pulse__bar--second"></span>
                        <span class="progress-option-pulse__bar progress-option-pulse__bar--third"></span>
                      </span>

                      <span *ngSwitchCase="'scan'" class="progress-option-scan">
                        <span class="progress-option-scan__grid"></span>
                        <span class="progress-option-scan__line"></span>
                      </span>

                      <span *ngSwitchCase="'trace'" class="progress-option-trace">
                        <span class="progress-option-trace__outline"></span>
                        <span class="progress-option-trace__corner"></span>
                        <span class="progress-option-trace__line"></span>
                      </span>

                      <span *ngSwitchCase="'steps'" class="progress-option-steps">
                        <span class="progress-option-steps__bar progress-option-steps__bar--one"></span>
                        <span class="progress-option-steps__bar progress-option-steps__bar--two"></span>
                        <span class="progress-option-steps__bar progress-option-steps__bar--three"></span>
                        <span class="progress-option-steps__bar progress-option-steps__bar--four"></span>
                      </span>
                    </ng-container>
                  </div>
                </div>
              </div>

              <div class="progress-showcase__section">
                <div class="progress-showcase__heading">Current Mark Motion</div>
                <div class="progress-showcase__grid progress-showcase__grid--brand">
                  <div *ngFor="let option of logoOptions" class="progress-showcase__item progress-showcase__item--logo">
                    <span class="progress-showcase__index">{{ option.id }}</span>
                    <span class="progress-showcase__name">{{ option.name }}</span>

                    <ng-container [ngSwitch]="option.kind">
                      <span *ngSwitchCase="'brand-orbit'" class="progress-option-logo progress-option-logo--orbit">
                        <span class="progress-brand-mark">
                          <span class="progress-brand-mark__roof"></span>
                          <span class="progress-brand-mark__frame"></span>
                          <span class="progress-brand-mark__door"></span>
                        </span>
                        <span class="progress-option-logo__ring progress-option-logo__ring--outer"></span>
                        <span class="progress-option-logo__ring progress-option-logo__ring--inner"></span>
                      </span>

                      <span *ngSwitchCase="'brand-glide'" class="progress-option-logo progress-option-logo--rail">
                        <span class="progress-option-logo__track"></span>
                        <span class="progress-brand-mark progress-brand-mark--rail">
                          <span class="progress-brand-mark__roof"></span>
                          <span class="progress-brand-mark__frame"></span>
                          <span class="progress-brand-mark__door"></span>
                        </span>
                      </span>

                      <span *ngSwitchCase="'brand-signal'" class="progress-option-logo progress-option-logo--signal">
                        <span class="progress-brand-mark progress-brand-mark--signal">
                          <span class="progress-brand-mark__roof"></span>
                          <span class="progress-brand-mark__frame"></span>
                          <span class="progress-brand-mark__door"></span>
                        </span>
                        <span class="progress-option-logo__signal progress-option-logo__signal--one"></span>
                        <span class="progress-option-logo__signal progress-option-logo__signal--two"></span>
                        <span class="progress-option-logo__signal progress-option-logo__signal--three"></span>
                      </span>

                      <span *ngSwitchCase="'brand-echo'" class="progress-option-logo progress-option-logo--echo">
                        <span class="progress-option-logo__echo"></span>
                        <span class="progress-brand-mark">
                          <span class="progress-brand-mark__roof"></span>
                          <span class="progress-brand-mark__frame"></span>
                          <span class="progress-brand-mark__door"></span>
                        </span>
                      </span>

                      <span *ngSwitchCase="'brand-scan'" class="progress-option-logo progress-option-logo--scan">
                        <span class="progress-brand-mark">
                          <span class="progress-brand-mark__roof"></span>
                          <span class="progress-brand-mark__frame"></span>
                          <span class="progress-brand-mark__door"></span>
                        </span>
                        <span class="progress-option-logo__scan"></span>
                      </span>

                      <span *ngSwitchCase="'brand-beacon'" class="progress-option-logo progress-option-logo--beacon">
                        <span class="progress-brand-mark">
                          <span class="progress-brand-mark__roof"></span>
                          <span class="progress-brand-mark__frame"></span>
                          <span class="progress-brand-mark__door"></span>
                        </span>
                        <span class="progress-option-logo__beam progress-option-logo__beam--left"></span>
                        <span class="progress-option-logo__beam progress-option-logo__beam--center"></span>
                        <span class="progress-option-logo__beam progress-option-logo__beam--right"></span>
                      </span>

                      <span *ngSwitchCase="'brand-halo'" class="progress-option-logo progress-option-logo--halo">
                        <span class="progress-option-logo__halo"></span>
                        <span class="progress-brand-mark">
                          <span class="progress-brand-mark__roof"></span>
                          <span class="progress-brand-mark__frame"></span>
                          <span class="progress-brand-mark__door"></span>
                        </span>
                      </span>

                      <span *ngSwitchCase="'brand-draft'" class="progress-option-logo progress-option-logo--draft">
                        <span class="progress-option-logo__draft-grid"></span>
                        <span class="progress-option-logo__draft-line"></span>
                        <span class="progress-brand-mark">
                          <span class="progress-brand-mark__roof"></span>
                          <span class="progress-brand-mark__frame"></span>
                          <span class="progress-brand-mark__door"></span>
                        </span>
                      </span>

                      <span *ngSwitchCase="'brand-build'" class="progress-option-logo progress-option-logo--build">
                        <span class="progress-brand-mark">
                          <span class="progress-brand-mark__roof"></span>
                          <span class="progress-brand-mark__frame"></span>
                          <span class="progress-brand-mark__door"></span>
                        </span>
                        <span class="progress-option-logo__pillar progress-option-logo__pillar--left"></span>
                        <span class="progress-option-logo__pillar progress-option-logo__pillar--center"></span>
                        <span class="progress-option-logo__pillar progress-option-logo__pillar--right"></span>
                      </span>

                      <span *ngSwitchCase="'brand-door'" class="progress-option-logo progress-option-logo--door">
                        <span class="progress-brand-mark">
                          <span class="progress-brand-mark__roof"></span>
                          <span class="progress-brand-mark__frame"></span>
                          <span class="progress-brand-mark__door"></span>
                        </span>
                        <span class="progress-option-logo__door-pulse"></span>
                      </span>

                      <span *ngSwitchCase="'brand-outline'" class="progress-option-logo progress-option-logo--outline">
                        <span class="progress-brand-mark">
                          <span class="progress-brand-mark__roof"></span>
                          <span class="progress-brand-mark__frame"></span>
                          <span class="progress-brand-mark__door"></span>
                        </span>
                        <span class="progress-option-logo__outline"></span>
                        <span class="progress-option-logo__outline-corner"></span>
                        <span class="progress-option-logo__outline-line"></span>
                      </span>
                    </ng-container>
                  </div>
                </div>
              </div>

              <div class="progress-showcase__section">
                <div class="progress-showcase__heading">CSS Rebrand Directions</div>
                <div class="progress-showcase__grid progress-showcase__grid--rebrand">
                  <div *ngFor="let option of rebrandOptions" class="progress-showcase__item progress-showcase__item--rebrand">
                    <span class="progress-showcase__index">{{ option.id }}</span>
                    <span class="progress-showcase__name">{{ option.name }}</span>

                    <ng-container [ngSwitch]="option.kind">
                      <span *ngSwitchCase="'logo-r-roof'" class="progress-brand-concept progress-brand-concept--r-roof">
                        <span class="progress-brand-concept__r-roof-roof"></span>
                        <span class="progress-brand-concept__r-roof-stem"></span>
                        <span class="progress-brand-concept__r-roof-bowl"></span>
                        <span class="progress-brand-concept__r-roof-leg"></span>
                      </span>

                      <span *ngSwitchCase="'logo-r-core'" class="progress-brand-concept progress-brand-concept--r-core">
                        <span class="progress-brand-concept__r-core-roof"></span>
                        <span class="progress-brand-concept__r-core-stem"></span>
                        <span class="progress-brand-concept__r-core-bowl"></span>
                        <span class="progress-brand-concept__r-core-cut"></span>
                        <span class="progress-brand-concept__r-core-leg"></span>
                      </span>

                      <span *ngSwitchCase="'logo-block-home'" class="progress-brand-concept progress-brand-concept--block-home">
                        <span class="progress-brand-concept__block-home-badge"></span>
                        <span class="progress-brand-concept__block-home-cut"></span>
                        <span class="progress-brand-concept__block-home-door"></span>
                      </span>

                      <span *ngSwitchCase="'logo-parcel-cut'" class="progress-brand-concept progress-brand-concept--parcel-cut">
                        <span class="progress-brand-concept__parcel-cut-tile"></span>
                        <span class="progress-brand-concept__parcel-cut-void"></span>
                        <span class="progress-brand-concept__parcel-cut-notch"></span>
                      </span>

                      <span *ngSwitchCase="'logo-contour'" class="progress-brand-concept progress-brand-concept--contour">
                        <span class="progress-brand-concept__contour-roof progress-brand-concept__contour-roof--back"></span>
                        <span class="progress-brand-concept__contour-body progress-brand-concept__contour-body--back"></span>
                        <span class="progress-brand-concept__contour-roof progress-brand-concept__contour-roof--front"></span>
                        <span class="progress-brand-concept__contour-body progress-brand-concept__contour-body--front"></span>
                      </span>

                      <span *ngSwitchCase="'logo-terrace'" class="progress-brand-concept progress-brand-concept--terrace">
                        <span class="progress-brand-concept__terrace-roof"></span>
                        <span class="progress-brand-concept__terrace-stack progress-brand-concept__terrace-stack--one"></span>
                        <span class="progress-brand-concept__terrace-stack progress-brand-concept__terrace-stack--two"></span>
                        <span class="progress-brand-concept__terrace-stack progress-brand-concept__terrace-stack--three"></span>
                        <span class="progress-brand-concept__terrace-stack progress-brand-concept__terrace-stack--four"></span>
                      </span>
                    </ng-container>
                  </div>
                </div>
              </div>
            </div>
          </ng-container>

          <ng-template #progressSelectedShell>
            <ng-container *ngIf="contentSelection === 'brand-halo'; else progressStandardShell">
              <div class="progress-content-choice progress-content-choice--brand-halo">
                <span class="progress-content-choice__halo"></span>
                <span class="progress-content-choice__aura progress-content-choice__aura--outer"></span>
                <span class="progress-content-choice__aura progress-content-choice__aura--inner"></span>
                <span class="progress-brand-mark progress-brand-mark--selected">
                  <span class="progress-brand-mark__roof"></span>
                  <span class="progress-brand-mark__frame"></span>
                  <span class="progress-brand-mark__door"></span>
                </span>
              </div>
            </ng-container>
          </ng-template>

          <ng-template #progressStandardShell>
            <div class="progress-content-shell">
              <span class="progress-content-glow progress-content-glow-start"></span>
              <span class="progress-content-glow progress-content-glow-end"></span>

              <div class="progress-drafting">
                <span class="progress-grid"></span>
                <span class="progress-beam progress-beam-left"></span>
                <span class="progress-beam progress-beam-center"></span>
                <span class="progress-beam progress-beam-right"></span>
                <span class="progress-roof"></span>
                <span class="progress-foundation"></span>
              </div>

              <div class="progress-sheet">
                <span class="progress-sheet-fold"></span>
                <span class="progress-sheet-edge"></span>
                <span class="progress-sheet-chip"></span>
                <span class="progress-sheet-line progress-sheet-line-strong"></span>
                <span class="progress-sheet-line progress-sheet-line-mid"></span>
                <span class="progress-sheet-line progress-sheet-line-short"></span>
                <span class="progress-sheet-dot progress-sheet-dot-top"></span>
                <span class="progress-sheet-dot progress-sheet-dot-bottom"></span>
                <span class="progress-meter">
                  <span class="progress-meter-track"></span>
                  <span class="progress-meter-value"></span>
                </span>
              </div>
            </div>
          </ng-template>
        </div>

        <span class="progress-screenreader">{{ label }}</span>
      </div>

      <div *ngSwitchDefault class="progress-overlay" role="status" aria-live="polite" [attr.aria-label]="label">
        <span class="progress-spinner" aria-hidden="true"></span>
        <span class="progress-screenreader">{{ label }}</span>
      </div>
    </ng-container>
  `,
  styles: []
})
export class ProgressIndicatorComponent {
  @Input() variant: ProgressIndicatorVariant = 'overlay';
  @Input() label = 'Loading...';
  @Input() appearance: ProgressIndicatorAppearance = 'elevated';
  @Input() size: ProgressIndicatorSize = 'regular';
  @Input() showcaseOptions = false;
  @Input() contentSelection: ProgressIndicatorSelection = 'standard';

  readonly conceptOptions: ReadonlyArray<{ id: string; name: string; kind: ProgressPreviewKind }> = [
    { id: '01', name: 'Sweep', kind: 'rail' },
    { id: '02', name: 'Pulse', kind: 'pulse' },
    { id: '03', name: 'Scan', kind: 'scan' },
    { id: '04', name: 'Trace', kind: 'trace' },
    { id: '05', name: 'Steps', kind: 'steps' },
  ];

  readonly logoOptions: ReadonlyArray<{ id: string; name: string; kind: ProgressPreviewKind }> = [
    { id: '06', name: 'Orbit', kind: 'brand-orbit' },
    { id: '07', name: 'Glide', kind: 'brand-glide' },
    { id: '08', name: 'Signal', kind: 'brand-signal' },
    { id: '09', name: 'Echo', kind: 'brand-echo' },
    { id: '10', name: 'Scan', kind: 'brand-scan' },
    { id: '11', name: 'Beacon', kind: 'brand-beacon' },
    { id: '12', name: 'Halo', kind: 'brand-halo' },
    { id: '13', name: 'Draft', kind: 'brand-draft' },
    { id: '14', name: 'Build', kind: 'brand-build' },
    { id: '15', name: 'Door', kind: 'brand-door' },
    { id: '16', name: 'Outline', kind: 'brand-outline' },
  ];

  readonly rebrandOptions: ReadonlyArray<{ id: string; name: string; kind: ProgressPreviewKind }> = [
    { id: '17', name: 'R Roof', kind: 'logo-r-roof' },
    { id: '18', name: 'R Core', kind: 'logo-r-core' },
    { id: '19', name: 'Block Home', kind: 'logo-block-home' },
    { id: '20', name: 'Parcel Cut', kind: 'logo-parcel-cut' },
    { id: '21', name: 'Contour', kind: 'logo-contour' },
    { id: '22', name: 'Terrace', kind: 'logo-terrace' },
  ];
}
